import { CarIdentifierWithIcon } from '@components/BookingFlow/Common/CarIdentifierWithIcon';
import { SalesItem } from '@components/BookingFlow/Mobile/salesItems/SalesItem';
import { ServiceCategoryCollapseCard } from '@components/BookingFlow/Mobile/salesItems/ServiceCategoryCollapseCard';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import type { CarWithSalesItemsForBookingRecord, SalesItemBookingCategoryType } from '@navio/nsdk';
import groupBy from 'lodash/groupBy';
import { BookingStoreCar, SALES_ITEM_BOOKING_FLOW_TYPE } from 'noddi-types';
import { AnimatedList, NoddiInfoBanner, NoddiLink } from 'noddi-ui-web';
import { compareLicensePlates, customerRoutes } from 'noddi-util';
import { useBookingSelectedSalesItemsCars } from 'noddi-web';

import { useCurrentBrand } from '../../../../stores/BrandStore/brandStore';
import { getCarName } from '../../../../utils/carName';

type CarWithSalesItemsProps = {
  salesItemsCar: CarWithSalesItemsForBookingRecord;
  /** Resolved by the parent from the draft, so this view never has to handle a car it can't find. */
  car: BookingStoreCar;
};

export const ServiceItemsForSingleCar = ({ salesItemsCar, car }: CarWithSalesItemsProps) => {
  const { licensePlate, salesItems } = salesItemsCar;

  const selectedSalesItemsCars = useBookingSelectedSalesItemsCars();
  const { brandEmail } = useCurrentBrand();

  const selectedSalesItemsCar = selectedSalesItemsCars.filter((item) =>
    compareLicensePlates(item.licensePlate, licensePlate)
  );

  const sortedSalesItems = [...salesItems].sort((a, b) => a.uiSortOrder - b.uiSortOrder);

  const bookingCategories = groupBy(sortedSalesItems, 'bookingCategoryType');

  const numberOfCategories = Object.keys(bookingCategories).length;

  const carName = getCarName({ car, showLicensePlate: true });

  const hasTireHotelWithoutServices = !numberOfCategories && (!!car.hasTireHotel || !!car.wheelStorage?.length);

  // Same situation the fleet selector handles with a warning banner — but a single-car booking
  // cannot drop the car and continue, so the way out is picking a different one.
  if (hasTireHotelWithoutServices) {
    return (
      <NoddiInfoBanner
        variant='warning'
        title={t`We do not provide tire hotel services in this area`}
        texts={[
          {
            value: t`${carName} has an active tire hotel contract, but we do not provide tire hotel services in this area - please contact us at ${brandEmail} if you need help with this`
          }
        ]}
        actionNode={
          <NoddiLink to={customerRoutes.newBookingCars.getPath()} size='small' variant='outline'>
            <Trans>Change car</Trans>
          </NoddiLink>
        }
      />
    );
  }

  if (!numberOfCategories) {
    return (
      <div className='flex flex-col gap-5'>
        <CarIdentifierWithIcon car={car} />
        <NoddiInfoBanner
          variant='info'
          title={t`No services available for this car`}
          texts={[{ value: t`We couldn't find any services you can book for this car at the selected address.` }]}
          actionNode={
            <>
              <NoddiLink to={customerRoutes.newBookingAddress.getPath()} size='small' variant='outline'>
                <Trans>Change address</Trans>
              </NoddiLink>
              <NoddiLink to={customerRoutes.newBookingCars.getPath()} size='small' variant='outline'>
                <Trans>Change car</Trans>
              </NoddiLink>
            </>
          }
        />
      </div>
    );
  }

  const onlyOneCategory = numberOfCategories === 1;

  return (
    <div className='flex flex-col gap-5'>
      <CarIdentifierWithIcon car={car} bookingCategories={Object.keys(bookingCategories)} />

      {onlyOneCategory ? (
        <AnimatedList delayBetweenItems={100}>
          {sortedSalesItems.map((item) => (
            <SalesItem
              key={item.salesItemId}
              type={SALES_ITEM_BOOKING_FLOW_TYPE.PRIMARY}
              licensePlate={licensePlate}
              salesItem={item}
            />
          ))}
        </AnimatedList>
      ) : (
        <AnimatedList delayBetweenItems={150}>
          {Object.entries(bookingCategories).map(([bookingCategoryType, salesItems]) => {
            const numberOfSelectedSalesItems = selectedSalesItemsCar.filter((item) =>
              salesItems.map((item) => item.salesItemId).includes(item.salesItemId)
            ).length;

            return (
              <ServiceCategoryCollapseCard
                key={bookingCategoryType}
                bookingCategoryType={bookingCategoryType as SalesItemBookingCategoryType}
                salesItems={salesItems}
                licensePlate={licensePlate}
                numberOfSelectedSalesItems={numberOfSelectedSalesItems}
                salesItemType={SALES_ITEM_BOOKING_FLOW_TYPE.PRIMARY}
              />
            );
          })}
        </AnimatedList>
      )}
    </div>
  );
};
